'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { X, Search, AlertCircle } from 'lucide-react';

interface IDSearchPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function IDSearchPopup({ isOpen, onClose }: IDSearchPopupProps) {
  const router = useRouter();
  const [searchId, setSearchId] = useState('');
  const [error, setError] = useState('');

  const validateAndSearch = () => {
    if (!searchId.trim()) {
      setError('Please enter an ID to search');
      return;
    }

    const upperCaseId = searchId.toUpperCase();
    
    // Check if ID starts with MVV (case insensitive)
    if (!upperCaseId.startsWith('MVV')) {
      setError('ID must start with MVV');
      return;
    }

    // Check if ID has proper format (MVV + numbers)
    const idPattern = /^MVV\d+$/;
    if (!idPattern.test(upperCaseId)) {
      setError('Invalid ID format. Use format: MVV followed by numbers');
      return;
    }

    setError('');
    onClose();
    router.push(`/search-results?id=${encodeURIComponent(upperCaseId)}`);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchId(value);
    setError(''); // Clear error when user types
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      validateAndSearch();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Search by ID</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <label htmlFor="searchId" className="block text-sm font-medium text-gray-700 mb-2">
              Enter Profile ID
            </label>
            <div className="relative">
              <input
                type="text"
                id="searchId"
                value={searchId}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="e.g., MVV100001"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg font-mono uppercase"
                autoFocus
              />
            </div>
            <p className="text-sm text-gray-500 mt-2">
              ID must start with &quot;MVV&quot; followed by numbers
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Search Button */}
          <button
            onClick={validateAndSearch}
            className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
          >
            <div className="flex items-center justify-center space-x-2">
              <Search className="w-5 h-5" />
              <span>Search Profile</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
